import React, { useEffect } from 'react';
import { ConfirmProvider } from 'material-ui-confirm';
import { Route, Navigate } from 'react-router-dom';
import { useApi, createRoutableExtension, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { CATALOG_FILTER_EXISTS } from '@backstage/catalog-client';

// Plugin instances for route bindings
import { catalogPlugin } from '@backstage/plugin-catalog';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import { catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { orgPlugin } from '@backstage/plugin-org';
import { techdocsPlugin } from '@backstage/plugin-techdocs';
import { catalogGraphPlugin } from '@backstage/plugin-catalog-graph';

// API and theme imports from refactored modules
import { graviteeAuthApiRef } from './api-refs';
import { apis } from './apis';
import { themes } from './theme';

// Plugin page components (still imported directly for rendering)
import { CatalogEntityPage } from '@backstage/plugin-catalog';
import { CatalogImportPage } from '@backstage/plugin-catalog-import';
import { SearchPage } from '@backstage/plugin-search';
import { TechDocsIndexPage, TechDocsReaderPage, TechDocsReaderLayout } from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { CollapsibleNavAddon } from './components/techdocs/CollapsibleNavAddon';
import { TechDocsEntityGate } from './components/TechDocsEntityGate';
import { userSettingsPlugin } from '@backstage/plugin-user-settings';
import { ForceGraph } from './components/catalog/ForceGraph';
import { notificationsPlugin } from '@backstage/plugin-notifications';
import { AnnouncementsPage } from '@backstage-community/plugin-announcements';

// Custom page components
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';

import { Root } from './components/Root';
import { HomeMenu } from './components/HomeMenu';
import { SectionMenu } from './components/SectionMenu';
import { LinksPage } from './components/LinksPage';
import { OperatorsPage } from './components/OperatorsPage';
import { AiMarketplacePage, AgentsPage, SkillsPage, PromptsPage, DashboardPage, FavoritesPage } from './components/AiMarketplace';
import { DatadogDashboardPage } from './components/Datadog/DatadogDashboardPage';
import { HarnessPipelinesPage } from './components/Harness/HarnessPipelinesPage';
import { HarnessProvider } from './components/Harness/HarnessContext';
import { QuayRepositoriesPage } from './components/Quay/QuayRepositoriesPage';
import { QuayProvider } from './components/Quay/QuayContext';
import { SubscriptionsPage } from './components/SubscriptionsPage';
import { ApplicationSearchPage } from './components/Apim';
import { IdeasPage } from '@internal/ideas';
import { TopologyViewerPage, TopologyBuilderPage, GesBuilderPage } from '@internal/topology-viewer';
import { ImpactAnalysisPage } from '@internal/impact-analysis';
import { GraviteeApiOperatorBuilderPage } from '@internal/gravitee-api-operator-builder';
import { AnalyticsDashboardPage } from '@internal/analytics';
import { TechRadarWrapper } from './components/techradar/TechRadarWrapper';
import { TechRadarLegendPage } from './components/techradar/TechRadarLegendPage';
import { TechRadarBlipPage } from './components/techradar/TechRadarBlipPage';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage as BackstageSignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { SignalsDisplay } from '@backstage/plugin-signals';

// Wraps the custom catalog page as a routable extension so catalog plugin
// routes link back correctly from entity pages.
const CatalogIndexPage = catalogPlugin.provide(
  createRoutableExtension({
    name: 'CustomCatalogIndexPage',
    mountPoint: catalogPlugin.routes.catalogIndex,
    component: () =>
      import('./components/catalog/CustomCatalogPage').then(
        m => m.CustomCatalogPage,
      ),
  }),
);

// Wraps the custom settings page as a routable extension so the
// settingsRouteRef is bound (required by the Settings sidebar item).
const CustomUserSettingsPage = userSettingsPlugin.provide(
  createRoutableExtension({
    name: 'CustomUserSettingsPage',
    mountPoint: userSettingsPlugin.routes.settingsPage,
    component: () =>
      import('./components/CustomSettingsPage').then(
        m => m.CustomSettingsPage,
      ),
  }),
);

/**
 * Temporary rollback to the in-portal legacy marketplace UI.
 * Keep this route wrapper so standalone mode can be re-enabled in one place.
 */
const MarketplaceRoute = ({ legacy: Legacy }: { legacy: React.ComponentType }) => {
  // Temporary rollback: force the in-portal legacy marketplace UI.
  // Standalone iframe mode remains in code for future re-enable, but is disabled here.
  return <Legacy />;
};

export const HomeRoute = () => {
  const configApi = useApi(configApiRef);
  // If no features are revealed yet (empty object or missing), go straight to AI Marketplace
  const sfr = configApi.getOptionalConfig('app.slowFeaturesReveal');
  const featureKeys = ['marketplace', 'harness', 'quay', 'datadog', 'catalog', 'documentation', 'ideas', 'links', 'announcements'];
  const hasAnyFeature = sfr
    ? featureKeys.some(key => sfr.getOptionalConfig(key)?.getOptionalBoolean('enabled'))
    : false;
  if (!hasAnyFeature) {
    return <Navigate to="/ai-marketplace" replace />;
  }
  return <HomeMenu />;
};

// Route bindings between plugins
export const bindPluginRoutes = ({ bind }: { bind: any }) => {
  // Catalog
  bind(catalogPlugin.externalRoutes, {
    viewTechDoc: techdocsPlugin.routes.docRoot,
  });

  // API Docs
  bind(apiDocsPlugin.externalRoutes, {
    registerApi: catalogImportPlugin.routes.importPage,
  });

  // Org
  bind(orgPlugin.externalRoutes, {
    catalogIndex: catalogPlugin.routes.catalogIndex,
  });

  // Catalog graph
  bind(catalogGraphPlugin.externalRoutes, {
    catalogEntity: catalogPlugin.routes.catalogEntity,
  });
};

const app = createApp({
  apis,
  themes,
  // EntityCatalogGraphCard is only rendered inside our own wrapper components,
  // so app-element traversal can no longer discover this plugin (and its
  // catalogGraphApiRef factory) on its own.
  plugins: [catalogGraphPlugin],
  components: {
    SignInPage: props => (
      <BackstageSignInPage
        {...props}
        auto
        align="center"
        title="Verint Developer Portal"
        providers={[
          {
            id: 'gravitee-auth-provider',
            title: 'Login to Hydra',
            message: 'You can use your Verint credentials',
            apiRef: graviteeAuthApiRef,
          },
        ]}
      />
    ),
  },
  bindRoutes: bindPluginRoutes,
});

const NotificationsIndexPage = notificationsPlugin.provide(
  createRoutableExtension({
    name: 'NotificationsPage',
    mountPoint: notificationsPlugin.routes.root,
    component: () => import('./components/Notifications/NotificationsWrapper').then(m => m.NotificationsWrapper),
  }),
);

// All application routes.
const routes = (
  <FlatRoutes>
    {/* Home and navigation routes */}
    <Route path="/" element={<HomeRoute />} />
    <Route path="/home" element={<HomeRoute />} />
    <Route path="/operators" element={<OperatorsPage />} />
    <Route path="/documentation-menu" element={<SectionMenu section="Documentation" />} />
    <Route path="/marketplace" element={<SectionMenu section="Marketplace" />} />
    <Route path="/our-tools-menu" element={<SectionMenu section="Hydra Tools" />} />
    <Route path="/okd-consoles" element={<SectionMenu section="OKD Consoles" />} />
    <Route path="/links" element={<LinksPage />} />
    
    {/* Catalog routes */}
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route path="/catalog/:namespace/:kind/:name" element={<CatalogEntityPage />}>
      {entityPage}
    </Route>
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/catalog-graph" element={
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', overflow: 'hidden' }}>
        <div style={{
          display: 'flex', alignItems: 'center', padding: '0 14px',
          height: 36, flexShrink: 0,
          background: '#1c2a3a', borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          <a
            href="/catalog"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              color: 'rgba(255,255,255,0.85)', textDecoration: 'none',
              fontSize: 13, fontWeight: 500,
            }}
          >
            ← Back to Catalog
          </a>
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          <ForceGraph />
        </div>
      </div>
    } />

    {/* Documentation routes */}
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route path="/docs/:namespace/:kind/:name/*" element={
      <TechDocsEntityGate>
        <TechDocsReaderPage>
          <TechDocsReaderLayout withHeader={false} />
        </TechDocsReaderPage>
      </TechDocsEntityGate>
    }>
      <TechDocsAddons>
        <CollapsibleNavAddon />
      </TechDocsAddons>
    </Route>
    {/* Tool and feature routes */}
    <Route path="/search" element={<SearchPage />}>{searchPage}</Route>
    <Route path="/ai-marketplace" element={<MarketplaceRoute legacy={AiMarketplacePage} />} />
    <Route path="/ai-marketplace/agents" element={<MarketplaceRoute legacy={AgentsPage} />} />
    <Route path="/ai-marketplace/skills" element={<MarketplaceRoute legacy={SkillsPage} />} />
    <Route path="/ai-marketplace/prompts" element={<MarketplaceRoute legacy={PromptsPage} />} />
    <Route path="/ai-marketplace/dashboard" element={<MarketplaceRoute legacy={DashboardPage} />} />
    <Route path="/ai-marketplace/favorites" element={<MarketplaceRoute legacy={FavoritesPage} />} />
    <Route path="/announcements" element={<AnnouncementsPage markdownRenderer="md-editor" />} />
    <Route path="/datadog" element={<DatadogDashboardPage />} />
    <Route path="/harness" element={<HarnessPipelinesPage />} />
    <Route path="/quay" element={<QuayRepositoriesPage />} />
    <Route path="/subscriptions" element={<SubscriptionsPage />} />
    <Route path="/apim" element={<Navigate to="/apim/applications" />} />
    <Route path="/apim/applications" element={<ApplicationSearchPage />} />
    <Route path="/ideas" element={<IdeasPage />} />
    <Route path="/topology-viewer" element={<TopologyViewerPage />} />
    <Route path="/impact-analysis" element={<ImpactAnalysisPage />} />
    <Route path="/topology-viewer/builder" element={<TopologyBuilderPage />} />
    <Route path="/ges-builder" element={<GesBuilderPage />} />
    <Route path="/gravitee-api-operator-builder" element={<GraviteeApiOperatorBuilderPage />} />
    <Route path="/analytics" element={<AnalyticsDashboardPage />} />
    <Route path="/tech-radar" element={<TechRadarWrapper />} />
    <Route path="/tech-radar/guide" element={<TechRadarLegendPage />} />
    <Route path="/tech-radar/blip/:slug" element={<TechRadarBlipPage />} />
    
    {/* User and miscellaneous routes */}
    <Route path="/settings/*" element={<CustomUserSettingsPage />} />
    <Route path="/notifications" element={<NotificationsIndexPage />} />
  </FlatRoutes>
);

/**
 * Catalog Prefetcher Component
 * 
 * Warms the CachingCatalogClient immediately after login so the catalog
 * page loads instantly on first visit. This performs a best-effort fetch
 * of catalog entities in the background.
 * 
 * Note: Auto-triggering OKD refresh on empty catalog is handled separately
 * by ClusterStatus component in CustomCatalogPage.
 */
export const CatalogPrefetcher = () => {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);

  useEffect(() => {
    const ENTITIES_REQUEST = {
      filter: { 'metadata.name': CATALOG_FILTER_EXISTS },
      order: [{ field: 'metadata.name', order: 'asc' as const }],
    };
    // Wait for identity to be resolved before fetching — ensures session cookie is set
    identityApi.getCredentials().then(() => {
      catalogApi.getEntities(ENTITIES_REQUEST).catch(() => {
        // Best-effort warm-up; failures are expected and handled
      });
    }).catch(() => {
      // Identity not available yet — skip prefetch
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  return null;
};

/**
 * App Root Component
 * 
 * Renders the full application with:
 * - Alert display for notifications
 * - OAuth request dialog for authentication flows
 * - Signals display for real-time updates
 * - App router with catalog prefetching
 * - Root layout with routes
 */
export const AppProviders = ({ children }: { children: React.ReactNode }) => {
  const configApi = useApi(configApiRef);
  const sfr = configApi.getOptionalConfig('app.slowFeaturesReveal');
  const showHarness  = sfr?.getOptionalConfig('harness')?.getOptionalBoolean('enabled')    ?? false;
  const showQuay     = sfr?.getOptionalConfig('quay')?.getOptionalBoolean('enabled')       ?? false;
  const showSubscriptions = sfr?.getOptionalConfig('subscriptions')?.getOptionalBoolean('enabled') ?? false;
  let wrapped = <>{children}</>;
  if (showHarness || showQuay || showSubscriptions) {
    wrapped = (
      <HarnessProvider>
        <QuayProvider>
              {wrapped}
        </QuayProvider>
      </HarnessProvider>
    );
  }
  return wrapped;
};

export function AnalyticsShortcut() {
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        window.location.href = '/analytics';
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  return null;
}

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AnalyticsShortcut />
    <AppRouter>
      <CatalogPrefetcher />
      <AppProviders>
        <ConfirmProvider>
          <Root>{routes}</Root>
        </ConfirmProvider>
      </AppProviders>
    </AppRouter>
  </>,
);
